import { formatEventDateTimeDisplay } from '@hit-app/convex/eventDateFormatting';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventPath } from '../lib/routes';
import { useEventReminderPopup, type UpcomingEventReminder } from '../lib/eventReminders';
import { useModalDialog } from '../lib/useModalDialog';

interface EventReminderDialogProps {
  enabled: boolean;
}

/** Zeigt beim App-Start ein zugesagtes Event, das innerhalb der nächsten 24h beginnt. */
export function EventReminderDialog({ enabled }: EventReminderDialogProps) {
  const { reminder, dismiss } = useEventReminderPopup(enabled);

  if (!reminder) return null;

  return <EventReminderDialogContent key={reminder.eventId} reminder={reminder} onClose={dismiss} />;
}

interface EventReminderDialogContentProps {
  reminder: UpcomingEventReminder;
  onClose: (action: 'dismissed' | 'opened') => void;
}

function EventReminderDialogContent({ reminder, onClose }: EventReminderDialogContentProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  // Merkt sich, ob der Dialog über "Zum Event" geschlossen wurde; onClose des dialog-Elements feuert danach
  const actionRef = useRef<'dismissed' | 'opened'>('dismissed');
  const { dateLabel, timeLabel } = formatEventDateTimeDisplay(reminder.startDate, reminder.endDate);

  useModalDialog(dialogRef);

  const closeDialog = () => dialogRef.current?.close();

  const openEvent = () => {
    actionRef.current = 'opened';
    closeDialog();
    void navigate(eventPath(reminder.eventId));
  };

  return (
    <dialog
      ref={dialogRef}
      className="whats-new-dialog"
      aria-labelledby="event-reminder-title"
      onClose={() => onClose(actionRef.current)}
      onClick={(event) => {
        // Klick auf den Backdrop trifft das dialog-Element selbst, nicht dessen Inhalt
        if (event.target === dialogRef.current) closeDialog();
      }}
    >
      <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 sm:px-8 sm:pt-8">
        <div className="min-w-0">
          <p className="whats-new-eyebrow">Erinnerung</p>
          <h2 id="event-reminder-title" className="mt-1 text-2xl tracking-tight text-balance">
            {reminder.name}
          </h2>
        </div>
        <button
          type="button"
          className="whats-new-close relative shrink-0"
          onClick={closeDialog}
          aria-label="Schliessen"
        >
          <svg viewBox="0 0 16 16" fill="currentColor" className="size-4" aria-hidden="true">
            <path d="M5.28 4.22a.75.75 0 0 0-1.06 1.06L6.94 8l-2.72 2.72a.75.75 0 1 0 1.06 1.06L8 9.06l2.72 2.72a.75.75 0 1 0 1.06-1.06L9.06 8l2.72-2.72a.75.75 0 0 0-1.06-1.06L8 6.94 5.28 4.22Z" />
          </svg>
          <span
            className="absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2 pointer-fine:hidden"
            aria-hidden="true"
          />
        </button>
      </header>

      <div className="px-6 pb-2 sm:px-8">
        <p className="font-semibold text-[var(--color-text-primary)]">{dateLabel}</p>
        <p className="mt-0.5 text-[var(--color-text-secondary)]">{timeLabel}</p>
        {reminder.address && <p className="mt-0.5 text-[var(--color-text-secondary)]">{reminder.address}</p>}
        <p className="mt-4 text-sm text-pretty text-[var(--color-text-secondary)]">
          Du hast für dieses Event zugesagt. Es beginnt in weniger als 24 Stunden.
        </p>
      </div>

      <footer className="flex flex-col gap-2 border-t border-[var(--color-divider)] px-6 py-5 sm:flex-row sm:px-8">
        <button type="button" className="btn btn-primary w-full sm:w-auto" onClick={openEvent}>
          Zum Event
        </button>
        <button type="button" className="btn btn-secondary w-full sm:w-auto" onClick={closeDialog}>
          Verstanden
        </button>
      </footer>
    </dialog>
  );
}
